"use client";

import React, { useState } from "react";
import Link from "next/link";
import {
  ArrowRight, Guitar, CheckCircle2, XCircle,
  AlertTriangle, Star, Users, GraduationCap,
  Lightbulb, Heart, ChevronRight, Music, Zap, Phone,
  Clock, MessageCircle,
} from "lucide-react";

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */

const COMPARISON = {
  acoustic: {
    label: "Guitar Acoustic",
    color: "#C8A96E",
    emoji: "🎸",
    pros: [
      "Âm vang lớn, không cần thiết bị thêm",
      "Phù hợp nhạc pop, folk, đệm hát",
      "Dễ tìm nhạc & tài liệu học online",
      "Thị trường đàn cũ đa dạng, dễ mua lại",
      "Lý tưởng nếu muốn hát kèm",
    ],
    cons: [
      "Dây thép đau tay hơn ở giai đoạn đầu",
      "Cần thời gian làm chai tay (2–4 tuần)",
    ],
    best: "Bạn muốn đệm hát, thích nhạc Việt, pop, ballad",
    price: "1.5 – 4 triệu (đàn cũ chất lượng)",
  },
  classic: {
    label: "Guitar Classic",
    color: "#22c55e",
    emoji: "🎼",
    pros: [
      "Dây nylon mềm, ít đau tay hơn khi mới học",
      "Tiếng ấm, dịu — phù hợp nhạc nhẹ",
      "Cần đàn rộng hơn, dễ bấm riêng từng nốt",
      "Phù hợp học nhạc lý, fingerpicking",
    ],
    cons: [
      "Âm nhỏ hơn, không hợp đệm hát sôi động",
      "Ít tài liệu nhạc Việt phổ biến hơn acoustic",
      "Cần đàn rộng hơi khó bấm hợp âm với tay nhỏ",
    ],
    best: "Bạn thích nhạc cổ điển, bossa nova, muốn học kỹ thuật",
    price: "1 – 3 triệu (đàn cũ chất lượng)",
  },
};

const PRICE_RANGES = [
  {
    range: "Dưới 1 triệu",
    verdict: "Không nên",
    verdictColor: "#ef4444",
    icon: XCircle,
    desc: "Hầu hết đàn trong tầm này có gỗ ép kém, cần đàn dễ cong, âm thanh khô và cứng. Học trên đàn xấu sẽ khiến bạn nản lòng rất nhanh — không phải do bạn thiếu năng khiếu.",
    tip: null,
  },
  {
    range: "1 – 2.5 triệu",
    verdict: "Phù hợp nhất",
    verdictColor: "#C8A96E",
    icon: CheckCircle2,
    desc: "Đây là vùng ngân sách lý tưởng cho người mới. Một cây đàn cũ Yamaha, Takamine hay Morris trong tầm này có chất lượng âm thanh và độ bền vượt xa đàn mới cùng giá.",
    tip: "💡 Gợi ý: Ưu tiên đàn cũ thương hiệu hơn đàn mới vô danh.",
  },
  {
    range: "2.5 – 5 triệu",
    verdict: "Rất tốt",
    verdictColor: "#22c55e",
    icon: Star,
    desc: "Nếu bạn nghiêm túc muốn gắn bó lâu dài, đây là vùng giá cho thấy sự cam kết. Đàn tốt giúp bạn nghe rõ sự tiến bộ hơn, và có thể dùng nhiều năm mà không cần nâng cấp sớm.",
    tip: "💡 Đàn cũ tầm này thường là hàng Nhật, Hàn — chất lượng xuất khẩu rất cao.",
  },
  {
    range: "Trên 5 triệu",
    verdict: "Chưa cần thiết",
    verdictColor: "#a1a1aa",
    icon: AlertTriangle,
    desc: "Người mới chưa phân biệt được sự khác biệt âm thanh ở tầm này. Hãy học vững trước, rồi nâng cấp khi bạn thực sự biết mình cần gì ở một cây đàn.",
    tip: null,
  },
];

const MISTAKES = [
  {
    no: "01",
    title: "Mua đàn chỉ vì nhìn đẹp hoặc giá rẻ",
    detail:
      "Màu sơn, hình dáng hay giá thấp không phản ánh chất lượng âm thanh. Nhiều người mua đàn dưới 500k rồi bỏ học sau 1 tháng vì đàn quá khó chơi — không phải do họ không có năng khiếu.",
    fix: "Ưu tiên: cần đàn thẳng, action vừa phải, thương hiệu có uy tín.",
  },
  {
    no: "02",
    title: "Mua online mà không kiểm tra trực tiếp",
    detail:
      "Ảnh sản phẩm không thể hiện tình trạng thực tế của đàn cũ. Cần cong, fret mòn, action cao — tất cả chỉ thấy được khi cầm tay và chơi thử.",
    fix: "Luôn xem tận mặt, hoặc nhờ người có kinh nghiệm đi cùng.",
  },
  {
    no: "03",
    title: "Chọn sai loại đàn so với phong cách muốn chơi",
    detail:
      "Nhiều người thích nhạc pop Việt nhưng lại mua guitar classic vì được tư vấn 'dễ học hơn'. Kết quả là chơi được nhưng không thoả mãn với âm thanh, mất động lực.",
    fix: "Xác định rõ: bạn muốn chơi nhạc gì? Rồi mới chọn loại đàn.",
  },
  {
    no: "04",
    title: "Bỏ qua việc setup đàn trước khi học",
    detail:
      "Đàn mới hay cũ đều có thể cần điều chỉnh action, cân lại cần đàn. Học trên đàn chưa được setup đúng sẽ đau tay hơn, âm thanh xấu hơn và dễ nản.",
    fix: "Sau khi mua, đưa đàn cho thợ setup một lần — tốn 100–200k nhưng rất xứng đáng.",
  },
  {
    no: "05",
    title: "Tự học hoàn toàn từ đầu mà không có định hướng",
    detail:
      "YouTube có vô vàn video nhưng không ai nhìn tư thế tay bạn. Nhiều người tự học 6 tháng với tư thế sai, sau đó phải bỏ nhiều thời gian hơn để sửa lại từ đầu.",
    fix: "Ít nhất 4–8 buổi đầu với giáo viên để xây nền kỹ thuật đúng.",
  },
];

const REASONS = [
  {
    icon: Users,
    title: "Cộng đồng 500+ học viên tại Sài Gòn",
    desc: "Không học một mình. Tham gia cộng đồng Guitar Sài Gòn — nơi hàng trăm người cùng hành trình, chia sẻ bài học, động viên lẫn nhau mỗi ngày.",
    highlight: false,
  },
  {
    icon: GraduationCap,
    title: "Học thử miễn phí – không cam kết trước",
    desc: "Một buổi học thử 100% miễn phí. Bạn trải nghiệm trực tiếp phương pháp, xem có phù hợp không rồi mới quyết định — không áp lực.",
    highlight: true,
  },
  {
    icon: Lightbulb,
    title: "Phương pháp từ người tự học thành công",
    desc: "Cường Guitar xuất phát từ con số 0, không nền tảng nhạc lý. Phương pháp dạy được xây dựng để giải quyết đúng khó khăn của người mới — không lý thuyết dài dòng.",
    highlight: false,
  },
  {
    icon: Heart,
    title: "Mua đàn và học cùng một nơi",
    desc: "Tư vấn chọn đàn phù hợp ngân sách → mua đàn đã kiểm tra kỹ → bắt đầu học ngay. Một hành trình liền mạch, không phải đi nhiều nơi.",
    highlight: false,
  },
];

/* ─────────────────────────────────────────
   SHARED UI
───────────────────────────────────────── */

const SectionBadge = ({ children }: { children: React.ReactNode }) => (
  <div
    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold border mb-5"
    style={{
      backgroundColor: "rgba(200,169,110,0.08)",
      borderColor: "rgba(200,169,110,0.35)",
      color: "var(--brand-primary)",
      fontFamily: "var(--font-montserrat)",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    }}
  >
    {children}
  </div>
);

const SectionHeading = ({
  children,
  center = true,
}: {
  children: React.ReactNode;
  center?: boolean;
}) => (
  <h2
    className={`text-2xl sm:text-3xl md:text-4xl font-black leading-tight tracking-tight mb-4 ${center ? "text-center" : ""}`}
    style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
  >
    {children}
  </h2>
);

const Divider = () => (
  <div className="flex items-center justify-center gap-3 my-14">
    <div className="h-px w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.2)" }} />
    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />
    <div className="h-px w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.2)" }} />
  </div>
);

/* ─────────────────────────────────────────
   COMPARISON TAB
───────────────────────────────────────── */
const ComparisonSection = () => {
  const [active, setActive] = useState<"acoustic" | "classic">("acoustic");
  const data = COMPARISON[active];
  const other = active === "acoustic" ? COMPARISON.classic : COMPARISON.acoustic;

  return (
    <section className="pb-6" aria-labelledby="comparison-heading">
      <div className="text-center mb-10">
        <SectionBadge>Câu hỏi đầu tiên của mọi người mới</SectionBadge>
        <SectionHeading>
          Acoustic Hay{" "}
          <span style={{ color: "var(--brand-primary)" }}>Classic</span>?
        </SectionHeading>
        <p className="text-sm sm:text-base max-w-xl mx-auto" style={{ color: "var(--muted-foreground)" }}>
          Không có câu trả lời đúng tuyệt đối — chỉ có loại đàn phù hợp với{" "}
          <strong style={{ color: "var(--foreground)" }}>mục tiêu và phong cách của bạn</strong>.
          Chọn tab để so sánh chi tiết.
        </p>
      </div>

      {/* Tab toggle */}
      <div
        className="flex rounded-2xl border p-1.5 max-w-sm mx-auto mb-8"
        style={{ backgroundColor: "var(--card)", borderColor: "var(--border)" }}
      >
        {(["acoustic", "classic"] as const).map((key) => {
          const isActive = active === key;
          const c = COMPARISON[key];
          return (
            <button
              key={key}
              onClick={() => setActive(key)}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-bold transition-all duration-200"
              style={{
                fontFamily: "var(--font-montserrat)",
                backgroundColor: isActive ? c.color : "transparent",
                color: isActive ? "#0D0D0D" : "var(--muted-foreground)",
                boxShadow: isActive ? `0 2px 12px ${c.color}40` : "none",
              }}
            >
              <span>{c.emoji}</span>
              {c.label.replace("Guitar ", "")}
            </button>
          );
        })}
      </div>

      {/* Content card */}
      <div
        className="rounded-3xl border overflow-hidden max-w-3xl mx-auto"
        style={{
          backgroundColor: "var(--card)",
          borderColor: `${data.color}40`,
          boxShadow: `0 4px 32px ${data.color}10`,
          transition: "border-color 0.3s, box-shadow 0.3s",
        }}
      >
        {/* Top bar */}
        <div className="h-1" style={{ backgroundColor: data.color }} />

        <div className="p-6 sm:p-8">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl border"
              style={{
                backgroundColor: `${data.color}15`,
                borderColor: `${data.color}30`,
              }}
            >
              {data.emoji}
            </div>
            <div>
              <h3
                className="font-black text-lg"
                style={{ fontFamily: "var(--font-montserrat)", color: data.color }}
              >
                {data.label}
              </h3>
              <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                Giá tham khảo đàn cũ: <strong style={{ color: "var(--foreground)" }}>{data.price}</strong>
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mb-6">
            {/* Pros */}
            <div>
              <p
                className="text-xs font-bold uppercase tracking-widest mb-3"
                style={{ color: "#22c55e", fontFamily: "var(--font-montserrat)" }}
              >
                ✅ Ưu điểm
              </p>
              <ul className="space-y-2">
                {data.pros.map((p) => (
                  <li key={p} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#22c55e" }} />
                    <span className="text-sm leading-snug" style={{ color: "var(--muted-foreground)" }}>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Cons */}
            <div>
              <p
                className="text-xs font-bold uppercase tracking-widest mb-3"
                style={{ color: "#f87171", fontFamily: "var(--font-montserrat)" }}
              >
                ⚠️ Lưu ý
              </p>
              <ul className="space-y-2">
                {data.cons.map((c) => (
                  <li key={c} className="flex items-start gap-2.5">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#f87171" }} />
                    <span className="text-sm leading-snug" style={{ color: "var(--muted-foreground)" }}>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Best for */}
          <div
            className="rounded-2xl px-5 py-4 border flex items-start gap-3"
            style={{
              backgroundColor: `${data.color}08`,
              borderColor: `${data.color}30`,
            }}
          >
            <Lightbulb className="w-4 h-4 shrink-0 mt-0.5" style={{ color: data.color }} />
            <div>
              <p
                className="text-xs font-bold uppercase tracking-wider mb-1"
                style={{ color: data.color, fontFamily: "var(--font-montserrat)" }}
              >
                Phù hợp nhất nếu...
              </p>
              <p className="text-sm" style={{ color: "var(--foreground)", fontWeight: 600 }}>
                {data.best}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick CTA to compare */}
      <p className="text-center text-sm mt-6" style={{ color: "var(--muted-foreground)" }}>
        Vẫn chưa chắc?{" "}
        <a
          href="tel:0325805893"
          className="font-bold underline decoration-dotted"
          style={{ color: "var(--brand-primary)" }}
        >
          Gọi Cường Guitar để được tư vấn miễn phí →
        </a>
      </p>
    </section>
  );
};

/* ─────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────── */
export default function GuitarChoNguoiMoiClient() {
  return (
    <main
      itemScope
      itemType="https://schema.org/WebPage"
      style={{
        backgroundColor: "var(--background)",
        color: "var(--foreground)",
        fontFamily: "var(--font-roboto)",
      }}
    >
      {/* ══════════════════════════════════════
          HERO
      ══════════════════════════════════════ */}
      <section
        className="relative overflow-hidden pt-16 pb-4 px-4 sm:px-6 lg:px-8"
        aria-labelledby="hero-heading"
      >
        {/* Background */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 80% 55% at 50% 0%, rgba(200,169,110,0.08) 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(rgba(200,169,110,0.055) 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />

        <div className="relative max-w-4xl mx-auto text-center">
          <SectionBadge>
            <GraduationCap className="w-3.5 h-3.5" />
            Dành cho người bắt đầu từ con số 0
          </SectionBadge>

          <h1
            id="hero-heading"
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-[1.1] tracking-tight mb-6"
            style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
          >
            Guitar Cho{" "}
            <span className="relative inline-block" style={{ color: "var(--brand-primary)" }}>
              Người Mới
              <span
                className="absolute left-0 -bottom-1 w-full h-0.5 rounded-full"
                style={{ backgroundColor: "var(--brand-primary)", opacity: 0.4 }}
              />
            </span>
            <br className="hidden sm:block" />
            — Chọn Đúng{" "}
            <span style={{ color: "var(--brand-primary)" }}>Ngay Từ Đầu</span>
          </h1>

          <p
            className="text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-8"
            style={{ color: "var(--muted-foreground)" }}
          >
            Acoustic hay classic? Bao nhiêu tiền là đủ? Tránh những lỗi nào?{" "}
            <strong style={{ color: "var(--foreground)" }}>
              Tất cả câu hỏi của người mới được trả lời thật thà
            </strong>{" "}
            — từ người từng ở đúng vị trí của bạn.
          </p>

          {/* Social proof */}
          <div
            className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl border mb-2"
            style={{
              backgroundColor: "var(--card)",
              borderColor: "rgba(200,169,110,0.25)",
            }}
          >
            <div className="flex -space-x-2">
              {["A", "B", "C", "D"].map((l) => (
                <div
                  key={l}
                  className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-black"
                  style={{
                    backgroundColor: "rgba(200,169,110,0.15)",
                    borderColor: "var(--background)",
                    color: "var(--brand-primary)",
                    fontFamily: "var(--font-montserrat)",
                  }}
                >
                  {l}
                </div>
              ))}
            </div>
            <div className="text-left">
              <p
                className="text-sm font-black"
                style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
              >
                500+ học viên đã bắt đầu
              </p>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 fill-current" style={{ color: "var(--brand-primary)" }} />
                ))}
                <span className="text-xs ml-1" style={{ color: "var(--muted-foreground)" }}>
                  tại Guitar Sài Gòn
                </span>
              </div>
            </div>
          </div>

  
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ══════════════════════════════════════
            ACOUSTIC VS CLASSIC
        ══════════════════════════════════════ */}
        <div id="acoustic-vs-classic">
          <ComparisonSection />
        </div>

        <Divider />

        {/* ══════════════════════════════════════
            PRICE GUIDE
        ══════════════════════════════════════ */}
        <section className="pb-6" aria-labelledby="price-heading">
          <div className="text-center mb-10">
            <SectionBadge>Ngân sách cho người mới</SectionBadge>
            <SectionHeading>
              Tầm Giá Nào{" "}
              <span style={{ color: "var(--brand-primary)" }}>Phù Hợp</span>?
            </SectionHeading>
            <p
              className="text-sm sm:text-base max-w-xl mx-auto"
              style={{ color: "var(--muted-foreground)" }}
            >
              Ngân sách không phải là tất cả — nhưng chọn sai tầm giá có thể khiến bạn bỏ cuộc sớm hơn bạn nghĩ.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {PRICE_RANGES.map(({ range, verdict, verdictColor, icon: Icon, desc, tip }) => (
              <div
                key={range}
                className="rounded-2xl border p-5 sm:p-6 flex flex-col gap-4"
                style={{
                  backgroundColor: "var(--card)",
                  borderColor: `${verdictColor}30`,
                }}
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p
                      className="text-lg font-black mb-0.5"
                      style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
                    >
                      {range}
                    </p>
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold"
                      style={{
                        backgroundColor: `${verdictColor}18`,
                        color: verdictColor,
                        fontFamily: "var(--font-montserrat)",
                      }}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {verdict}
                    </span>
                  </div>
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border"
                    style={{
                      backgroundColor: `${verdictColor}10`,
                      borderColor: `${verdictColor}25`,
                    }}
                  >
                    <Icon className="w-5 h-5" style={{ color: verdictColor }} />
                  </div>
                </div>

                <p className="text-sm leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
                  {desc}
                </p>

                {tip && (
                  <div
                    className="rounded-xl px-4 py-3 border text-xs leading-relaxed"
                    style={{
                      backgroundColor: "rgba(200,169,110,0.06)",
                      borderColor: "rgba(200,169,110,0.2)",
                      color: "var(--muted-foreground)",
                    }}
                  >
                    {tip}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <Divider />

        {/* ══════════════════════════════════════
            MISTAKES
        ══════════════════════════════════════ */}
        <section className="pb-6" aria-labelledby="mistakes-heading">
          <div className="text-center mb-10">
            <SectionBadge>Học từ sai lầm của người khác</SectionBadge>
            <SectionHeading>
              5 Lỗi Người Mới{" "}
              <span style={{ color: "#ef4444" }}>Hay Mắc Phải</span>
            </SectionHeading>
            <p
              className="text-sm sm:text-base max-w-xl mx-auto"
              style={{ color: "var(--muted-foreground)" }}
            >
              Những lỗi này không hiếm — chúng xảy ra với hầu hết người mới học guitar. Biết trước để tránh, học nhanh hơn.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {MISTAKES.map((m) => (
              <div
                key={m.no}
                className="rounded-2xl border overflow-hidden"
                style={{ backgroundColor: "var(--card)", borderColor: "var(--border)" }}
              >
                <div className="p-5 sm:p-6 flex gap-4">
                  {/* Number */}
                  <div
                    className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-xs font-black"
                    style={{
                      fontFamily: "var(--font-montserrat)",
                      backgroundColor: "rgba(239,68,68,0.1)",
                      color: "#ef4444",
                      border: "1.5px solid rgba(239,68,68,0.25)",
                    }}
                  >
                    {m.no}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3
                      className="font-black text-sm sm:text-base mb-2"
                      style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
                    >
                      {m.title}
                    </h3>
                    <p className="text-sm leading-relaxed mb-3" style={{ color: "var(--muted-foreground)" }}>
                      {m.detail}
                    </p>
                    <div
                      className="inline-flex items-start gap-2 px-3 py-2 rounded-xl text-xs font-semibold border"
                      style={{
                        backgroundColor: "rgba(200,169,110,0.07)",
                        borderColor: "rgba(200,169,110,0.22)",
                        color: "var(--brand-primary)",
                      }}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      {m.fix}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <Divider />

        {/* ══════════════════════════════════════
            REASONS / WHY GUITAR SAIGON
        ══════════════════════════════════════ */}
        <section className="pb-6" aria-labelledby="reasons-heading">
          <div className="text-center mb-10">
            <SectionBadge>Tại sao chọn Guitar Sài Gòn?</SectionBadge>
            <SectionHeading>
              Bắt Đầu Đúng Nơi —{" "}
              <span style={{ color: "var(--brand-primary)" }}>Tiến Bộ Nhanh Hơn</span>
            </SectionHeading>
            <p
              className="text-sm sm:text-base max-w-xl mx-auto"
              style={{ color: "var(--muted-foreground)" }}
            >
              Hơn 500 học viên đã chọn Guitar Sài Gòn làm điểm xuất phát. Đây là lý do.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
            {REASONS.map(({ icon: Icon, title, desc, highlight }) => (
              <div
                key={title}
                className="rounded-2xl border p-6 flex gap-4 transition-all duration-200"
                style={{
                  backgroundColor: highlight ? "rgba(200,169,110,0.06)" : "var(--card)",
                  borderColor: highlight ? "rgba(200,169,110,0.45)" : "var(--border)",
                  boxShadow: highlight ? "0 4px 24px rgba(200,169,110,0.08)" : "none",
                }}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border"
                  style={{
                    backgroundColor: "rgba(200,169,110,0.1)",
                    borderColor: "rgba(200,169,110,0.25)",
                  }}
                >
                  <Icon className="w-5 h-5" style={{ color: "var(--brand-primary)" }} />
                </div>
                <div>
                  <h3
                    className="font-black text-sm mb-1.5"
                    style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
                  >
                    {title}
                    {highlight && (
                      <span
                        className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold"
                        style={{
                          backgroundColor: "var(--brand-primary)",
                          color: "var(--brand-primary-foreground)",
                        }}
                      >
                        HOT
                      </span>
                    )}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
                    {desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Final CTA block */}
          <div
            className="rounded-3xl border p-8 sm:p-10 text-center relative overflow-hidden"
            style={{
              backgroundColor: "var(--card)",
              borderColor: "rgba(200,169,110,0.35)",
              boxShadow: "0 0 60px rgba(200,169,110,0.07)",
            }}
          >
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(200,169,110,0.07) 0%, transparent 70%)",
              }}
            />
            <div className="relative">
              <div
                className="h-1 w-10 rounded-full mx-auto mb-6"
                style={{ backgroundColor: "var(--brand-primary)" }}
              />
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold border mb-4"
                style={{
                  backgroundColor: "rgba(200,169,110,0.08)",
                  borderColor: "rgba(200,169,110,0.35)",
                  color: "var(--brand-primary)",
                  fontFamily: "var(--font-montserrat)",
                }}
              >
                <Music className="w-3.5 h-3.5" />
                Bắt đầu hành trình guitar
              </div>
              <h3
                className="text-xl sm:text-2xl md:text-3xl font-black mb-3 leading-tight"
                style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
              >
                Sẵn sàng bắt đầu chưa?
              </h3>
              <p
                className="text-sm sm:text-base mb-8 max-w-md mx-auto"
                style={{ color: "var(--muted-foreground)" }}
              >
                Ghé Guitar Sài Gòn — xem đàn, học thử miễn phí, tư vấn tận tâm.{" "}
                <strong style={{ color: "var(--foreground)" }}>Không cần biết gì trước</strong>.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  href="/dang-ky-hoc-guitar"
                  className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-black text-sm transition-all hover:opacity-90 active:scale-[0.98] w-full sm:w-auto justify-center"
                  style={{
                    fontFamily: "var(--font-montserrat)",
                    backgroundColor: "var(--brand-primary)",
                    color: "var(--brand-primary-foreground)",
                    boxShadow: "0 4px 20px rgba(200,169,110,0.3)",
                  }}
                >
                  <Zap className="w-4 h-4" />
                  Đăng ký học thử miễn phí
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>

                <a
                  href="tel:0325805893"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-sm border transition-all w-full sm:w-auto justify-center"
                  style={{
                    fontFamily: "var(--font-montserrat)",
                    borderColor: "rgba(200,169,110,0.35)",
                    color: "var(--brand-primary)",
                    backgroundColor: "rgba(200,169,110,0.06)",
                  }}
                >
                  <Phone className="w-4 h-4" />
                  Gọi tư vấn ngay
                </a>
              </div>

              {/* Trust row */}
              <div
                className="flex flex-wrap items-center justify-center gap-4 mt-8 pt-6 border-t"
                style={{ borderColor: "var(--border)" }}
              >
                {[
                  { icon: Clock, text: "Mở cửa 18:00 – 22:00" },
                  { icon: Users, text: "500+ học viên" },
                  { icon: MessageCircle, text: "Tư vấn miễn phí" },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5" style={{ color: "var(--brand-primary)" }} />
                    <span className="text-xs font-semibold" style={{ color: "var(--muted-foreground)" }}>
                      {text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Bottom spacing */}
        <div className="pb-16" />
      </div>
    </main>
  );
}